/**
 * Contact classification — the v1 deterministic rule set (exchange-social-track,
 * Decided Design #8). Best-effort: recognize clear contact patterns; everything
 * else is chat. No garbage on the card — a miss returns null, never a guess record.
 *
 * Rules, in order:
 *   1. Phone-shaped (only phone chars, 7–15 digits) → phone
 *      (WhatsApp only when reciprocity clearly points there: peer shared
 *      whatsapp and NOT phone)
 *   2. Email-shaped → email
 *   3. @-leading handle → at-prefix type via reciprocal context (default instagram)
 *   4. Field-state assist: a plausible bare handle while the field is
 *      icon-specialized to Instagram → instagram
 *   5. Single uncommon word (one token, has a letter, handle-safe chars,
 *      not conversational) → account-name guess via reciprocal context
 *
 * Sentence-embedded CONTACT extraction ("hey here's mine, dimitri") is
 * deliberately OUT of scope — that's Contact inference v2 (its own plan).
 * NAME-greeting extraction (extractNameFromEntry below) is the one sentence
 * rule that shipped early (b/exchange-polish, 2026-07-21): people copy the
 * Profile's greeting shape into the name field, and the pattern is closed.
 */

import { CONTACT_FIELDS, getBaseType } from './contactKeys';

// Conversational words that must never classify as an account handle.
// Not a dictionary — just the words people actually type in a first chat.
const COMMON_WORDS = new Set([
  'hi',
  'hey',
  'heyy',
  'hello',
  'yo',
  'sup',
  'howdy',
  'thanks',
  'thank',
  'thx',
  'ty',
  'please',
  'sorry',
  'oops',
  'congrats',
  'cheers',
  'yes',
  'yeah',
  'yep',
  'yup',
  'no',
  'nope',
  'nah',
  'ok',
  'okay',
  'k',
  'kk',
  'cool',
  'nice',
  'great',
  'awesome',
  'perfect',
  'sweet',
  'dope',
  'sick',
  'word',
  'bet',
  'haha',
  'hahaha',
  'hehe',
  'lol',
  'lmao',
  'omg',
  'wow',
  'damn',
  'sure',
  'maybe',
  'later',
  'soon',
  'tomorrow',
  'today',
  'tonight',
  'morning',
  'night',
  'bye',
  'goodbye',
  'ciao',
  'peace',
  'love',
  'u',
  'you',
  'me',
  'we',
  'same',
  'right',
  'true',
  'good',
  'bad',
  'fine',
  'well',
  'really',
  'very',
  'just',
  'also',
  'and',
  'but',
  'for',
  'the',
  'this',
  'that',
  'here',
  'there',
  'now',
  'then',
  'what',
  'when',
  'where',
  'why',
  'how',
  'who',
  'totally',
  'definitely',
  'absolutely',
  'exactly',
  'literally',
  'honestly',
  'monday',
  'tuesday',
  'wednesday',
  'thursday',
  'friday',
  'saturday',
  'sunday',
  'mon',
  'tue',
  'tues',
  'wed',
  'thu',
  'thur',
  'thurs',
  'fri',
  'sat',
  'sun',
]);

// Only these characters can appear in something phone-shaped
const PHONE_CHARS = /^[+()\-.\s\d]+$/;
const EMAIL_PATTERN = /^\S+@\S+\.\S+$/;
// Account-handle body: letters/digits/dot/underscore, 2–30 chars
const HANDLE_BODY = /^[a-z0-9._]{2,30}$/i;
const HAS_LETTER = /[a-z]/i;

// Handle-guess priority among the @-prefix types when reciprocity offers several
const AT_PREFIX_PRIORITY = ['instagram', 'tiktok', 'twitter'];

/**
 * The peer's shared contact types, cleaned for reciprocity decisions:
 * secondary variants collapse to their base type, non-contact fields drop.
 */
export const reciprocalContactTypes = (profileData) => {
  const sharedFields = profileData?.sharedFields || [];
  return [...new Set(sharedFields.map(getBaseType).filter((f) => CONTACT_FIELDS.includes(f)))];
};

const pickHandleType = (reciprocalTypes) =>
  AT_PREFIX_PRIORITY.find((t) => reciprocalTypes.includes(t)) || 'instagram';

/**
 * Classify one submission from the contact field.
 *
 * @param {string} raw - Exactly what the person typed (the bubble shows this verbatim)
 * @param {Object} [context]
 * @param {'anything'|'phone'|'insta'} [context.fieldState] - Which field state was active
 * @param {string[]} [context.reciprocalTypes] - From reciprocalContactTypes(peer profileData)
 * @returns {{type: string, value: string}|null} Cleaned record value, or null = pure chat
 */
export const classifyContactInput = (
  raw,
  { fieldState = 'anything', reciprocalTypes = [] } = {}
) => {
  const text = (raw || '').trim();
  if (!text) return null;

  // 1. Phone-shaped
  const digits = text.replace(/\D/g, '');
  if (PHONE_CHARS.test(text) && digits.length >= 7 && digits.length <= 15) {
    const type =
      reciprocalTypes.includes('whatsapp') && !reciprocalTypes.includes('phone')
        ? 'whatsapp'
        : 'phone';
    return { type, value: text };
  }

  // 2. Email-shaped
  if (EMAIL_PATTERN.test(text)) {
    return { type: 'email', value: text.toLowerCase() };
  }

  // 3. @-leading handle — the @ is the strongest account signal
  if (text.startsWith('@') && HANDLE_BODY.test(text.slice(1)) && HAS_LETTER.test(text)) {
    return { type: pickHandleType(reciprocalTypes), value: text.slice(1).toLowerCase() };
  }

  // Anything below needs a single handle-safe token with at least one letter
  const isBareToken =
    HANDLE_BODY.test(text) && HAS_LETTER.test(text) && !COMMON_WORDS.has(text.toLowerCase());
  if (!isBareToken) return null;

  // 4. Field-state assist: they tapped the Instagram icon, then typed a plausible handle
  if (fieldState === 'insta') {
    return { type: 'instagram', value: text.toLowerCase() };
  }

  // 5. Single uncommon word → account-name guess (user ruling, /refine pass 1)
  return { type: pickHandleType(reciprocalTypes), value: text.toLowerCase() };
};

// ===== Name-greeting extraction (v1, b/exchange-polish 2026-07-21) =====

// The intro VERB is the trigger, ANYWHERE in the text (device iteration
// 2026-07-21: "Hey what's up it's Sarah" — the anchored version required the
// verb right after the greeting and missed chatty prefixes). The LAST
// occurrence wins ("it's me, it's Sarah" → Sarah); everything after it must
// pass the name guards below. A verb is still required — "hi Franklin" could
// be greeting the Profile by name, so it never extracts.
const NAME_INTRO = /\b(?:it'?s|i'?m|im|i am|this is|my name'?s|my name is|name'?s|name is)\s+/gi;
// Chat-tightened triggers for the refinement pass (two-message pattern):
// "this is X" / "i am X" are common speech in chat ("this is fun") — only the
// self-referential shapes survive there.
const NAME_INTRO_CHAT = /\b(?:it'?s|i'?m|im|my name'?s|my name is|name'?s|name is)\s+/gi;
// The trailing-marker intro — "Sally here" / "Heya Sally here" (device request
// 2026-07-21). Stripped from the candidate in BOTH rules, so "it's Sally here"
// works too. A leading greeting word is stripped before the guards.
const HERE_TAIL = /[\s,!]+here[\s,.!?~-]*$/i;
// One name word: letters (any script), with ' . - allowed inside
const NAME_WORD = /^[\p{L}][\p{L}'.-]*$/u;
const TRAILING_PUNCT = /[\s,.!?~-]+$/;
// Clause words that mean the remainder is a sentence, not a name — includes
// the "___ here" phrases that must never extract (come here, almost here,
// she's already here…)
const NAME_STOP_WORDS = new Set([
  'from',
  'at',
  'in',
  'on',
  'btw',
  'again',
  'nice',
  'come',
  'over',
  'almost',
  'still',
  'out',
  'down',
  'up',
  'back',
  'already',
  'is',
  'was',
  'been',
  'are',
  'am',
  'be',
  'gonna',
  'going',
  'coming',
  'get',
  'got',
  'getting',
  'waiting',
  'arrived',
  'stuck',
  // Greeting variants COMMON_WORDS doesn't carry (that set is the
  // classifier's own list — kept untouched to leave its behavior alone)
  'heya',
  'hiya',
  'wassup',
  'wasup',
  'whatsup',
  'waasup',
]);

// iOS smart punctuation types U+2019 (') for the apostrophe — "it's" with a
// curly quote must match "it'?s" (device miss, 2026-07-21: "Hey it's Sammy
// McGoo" stored whole). Normalized everywhere, including the stored name —
// ASCII apostrophes travel better (vCards, autofill).
const normalizeApostrophes = (s) => s.replace(/[‘’ʼ`]/g, "'");

// THE name-word rule, one home (was copy-pasted three times — /simplify):
// name-shaped, not conversational, not a clause word.
const isNameShapedWord = (w) =>
  NAME_WORD.test(w) && !COMMON_WORDS.has(w.toLowerCase()) && !NAME_STOP_WORDS.has(w.toLowerCase());

/**
 * Extract a name from a sentence-style name-field entry — "Hi it's Franklin"
 * → "Franklin". The bubble always shows the raw words (contract #9b); this
 * only refines what the RECORD stores. Same philosophy as the classifier:
 * a miss returns null (caller keeps the raw text), never a guess.
 *
 * All-lowercase words get capitalized ("franklin" → "Franklin"); mixed case
 * is kept as typed. Anything beyond this shape — no intro verb, >3 words,
 * digits, clause words — is Contact inference v2's problem.
 *
 * @param {string} raw - Exactly what the person typed
 * @param {Object} [options]
 * @param {boolean} [options.fromChat=false] - Refinement pass over a chat
 *   message (two-message pattern): tighter trigger set, same guards
 * @returns {string|null} The extracted name, or null = store the raw text
 */
export const extractNameFromEntry = (raw, { fromChat = false } = {}) => {
  const text = normalizeApostrophes((raw || '').trim());
  if (!text) return null;

  // Rule 1 — intro verb, last occurrence wins (matchAll: /g stays stateless).
  // Rule 2 — trailing "here" marker: scan BACKWARDS from "here" and take the
  // run of name-shaped words directly before it — everything earlier is
  // filler by construction ("Hey what's up Emily here" → Emily), so the rule
  // is phrasing-independent instead of chasing greeting variants. Ambiguity
  // guard: a run that hits the 3-word cap with more name-shaped words before
  // it is a truncation risk, not a name — return null.
  // Both candidates lose a trailing "here" before the guards, so
  // "it's Sally here" and "Heya Sally here" both land on "Sally".
  // A bare greeting + name ("hi Sally") stays out — could be greeting the peer.
  let rest = null;
  const intros = [...text.matchAll(fromChat ? NAME_INTRO_CHAT : NAME_INTRO)];
  if (intros.length > 0) {
    const intro = intros[intros.length - 1];
    rest = text.slice(intro.index + intro[0].length);
  } else if (HERE_TAIL.test(text)) {
    const words = text.replace(HERE_TAIL, '').trim().split(/\s+/);
    const run = [];
    let i = words.length - 1;
    for (; i >= 0 && run.length < 3; i--) {
      if (!isNameShapedWord(words[i])) break;
      run.unshift(words[i]);
    }
    if (run.length === 0) return null;
    if (run.length === 3 && i >= 0 && isNameShapedWord(words[i])) return null;
    rest = run.join(' ');
  } else {
    return null;
  }

  rest = rest.replace(HERE_TAIL, '').replace(TRAILING_PUNCT, '').trim();
  if (!looksLikePlainName(rest)) return null;

  return rest
    .split(/\s+/)
    .map((w) => (w === w.toLowerCase() ? w.charAt(0).toUpperCase() + w.slice(1) : w))
    .join(' ');
};

/**
 * Does the raw text read as a plain name on its own? ("Emily", "Bri Larsen")
 * Used to decide whether a stored name is PROVISIONAL: a first entry that
 * extracted nothing AND fails this check ("Hey Mark") is known-garbage, which
 * opens the two-message refinement window — a later chat message that
 * extracts replaces it. A name that passes here is never touched.
 */
export const looksLikePlainName = (raw) => {
  const text = normalizeApostrophes((raw || '').trim()).replace(TRAILING_PUNCT, '');
  if (!text) return false;
  const words = text.split(/\s+/);
  if (words.length > 3) return false;
  return words.every(isNameShapedWord);
};
